/**
 * Kalkulator prawidłowej wagi DNŻ: czysta logika obliczeń.
 *
 * Zero DOM, zero zależności. Ten sam plik działa w przeglądarce
 * (window.DnnzIdealWeightLogic) i w Node (module.exports), więc testy
 * sprawdzają dokładnie ten kod, który liczy na stronie.
 *
 * Wszystkie liczby przychodzą z konfiguracji inc/data/ideal-weight.php (w szablonie
 * atrybut data-config, w testach parser tablicy PHP). W tym pliku nie ma
 * żadnej wartości merytorycznej. Źródła: ZRODLA.md, decyzje: DECYZJE.md.
 *
 * Zakres: BMI 18,5 do 24,9 × wzrost² dla wszystkich dorosłych (WHO, D1).
 * Od 65 lat zmienia się tylko jedna linia pod kafelkiem: dolny próg BMI 23
 * (Winter 2014) albo uwaga o odchudzaniu (D3). Płeć nie wpływa na nic (D11).
 * Jedyne sprawdzenie wejścia: czy takie wartości zanotowano u dorosłego
 * człowieka (D10).
 */
(function (root, factory) {
	'use strict';
	var api = factory();
	if (typeof module === 'object' && module.exports) {
		module.exports = api;
	}
	if (root) {
		root.DnnzIdealWeightLogic = api;
	}
}(typeof window !== 'undefined' ? window : null, function () {
	'use strict';

	// --- Liczby ---------------------------------------------------------

	function toNumber(value) {
		if (value === null || value === '' || typeof value === 'undefined') {
			return NaN;
		}
		var parsed = Number(String(value).trim().replace(',', '.'));
		return isFinite(parsed) ? parsed : NaN;
	}

	/**
	 * Ucina śmieci zmiennoprzecinkowe przed zaokrągleniem: 24,9 × 1,7² to
	 * w IEEE 754 71,96099999999998, a 18,5 × 2² to 74,00000000000001.
	 * Dziesięć miejsc, nie sześć: przy wzroście podanym co 0,1 cm iloczyn
	 * BMI × wzrost² ma do 9 miejsc po przecinku, a iloraz masy i wzrostu²
	 * potrafi leżeć 4 × 10⁻⁷ od połówki (73,6 kg / 1,735² = 24,4499996).
	 */
	function clean(value) {
		return Math.round(value * 1e10) / 1e10;
	}

	function round1(value) {
		return Math.round(clean(value) * 10) / 10;
	}

	/**
	 * Jedno miejsce po przecinku, zawsze: 24.9 → "24,9", 23 → "23,0".
	 */
	function formatDecimal(value) {
		var tenths = Math.round(value * 10);
		var sign = tenths < 0 ? '-' : '';
		tenths = Math.abs(tenths);
		return sign + Math.floor(tenths / 10) + ',' + (tenths % 10);
	}

	/**
	 * Kilogramy i centymetry: bez ",0" przy liczbie całkowitej, 9.4 → "9,4", 9 → "9".
	 */
	function formatNumber(value) {
		var tenths = Math.round(value * 10);
		return tenths % 10 === 0 ? String(tenths / 10) : formatDecimal(value);
	}

	/**
	 * Odczyt pola liczbowego: { value, empty, invalid }. Puste pole to osobny
	 * stan, nie błąd. Błąd pola = tylko tekst, który nie jest liczbą dodatnią (D10).
	 */
	function readField(raw) {
		var text = raw === null || typeof raw === 'undefined' ? '' : String(raw).trim();
		if (text === '') {
			return { value: null, empty: true, invalid: false };
		}
		var value = toNumber(text);
		if (isNaN(value) || value <= 0) {
			return { value: null, empty: false, invalid: true };
		}
		return { value: value, empty: false, invalid: false };
	}

	// --- Zakres -----------------------------------------------------------

	/**
	 * Wyświetlane granice zakresu w pełnych kg (D1): dolna w górę, górna w dół.
	 */
	function weightRange(heightCm, cfg) {
		var h = heightCm / 100;
		return {
			low: Math.ceil(clean(cfg.range.bmi_low * h * h)),
			high: Math.floor(clean(cfg.range.bmi_high * h * h))
		};
	}

	/**
	 * Dolny próg od 65 lat w pełnych kg: BMI 23 × wzrost², w górę (D3).
	 */
	function seniorFloor(heightCm, cfg) {
		var h = heightCm / 100;
		return Math.ceil(clean(cfg.senior.floor_bmi * h * h));
	}

	/**
	 * Jedna linia pod kafelkiem (D3, D8), najwyżej jedna:
	 * - poniżej 65 lat: 'below' tylko przy masie poniżej zakresu;
	 * - od 65 lat: 'senior-floor' bez masy, 'senior-low' przy masie poniżej progu
	 *   (także w zakresie; zastępuje 'below'), 'senior-over' powyżej zakresu przy
	 *   BMI poniżej 30, 'senior-obese' od BMI 30, w zakresie od progu brak linii.
	 */
	function note(senior, weight, heightCm, status, floor, cfg) {
		if (!senior) {
			return status === 'below' ? 'below' : null;
		}
		if (weight === null) {
			return 'senior-floor';
		}
		if (weight < floor) {
			return 'senior-low';
		}
		if (status === 'above') {
			var h = heightCm / 100;
			return clean(weight / (h * h)) < cfg.senior.obese_bmi ? 'senior-over' : 'senior-obese';
		}
		return null;
	}

	/**
	 * Położenie masy wobec wyświetlonych granic i różnica w kg (D8).
	 * W zakresie różnicy nie pokazujemy (kafelek „W zakresie” bez kg).
	 */
	function compare(weight, range) {
		if (weight < range.low) {
			return { status: 'below', diff: round1(range.low - weight) };
		}
		if (weight > range.high) {
			return { status: 'above', diff: round1(weight - range.high) };
		}
		return { status: 'inside', diff: null };
	}

	// --- Ocena całego wejścia -----------------------------------------------

	/**
	 * input: { age, height, weight (teksty pól) }
	 */
	function evaluate(input, cfg) {
		var fields = {
			age: readField(input.age),
			height: readField(input.height),
			weight: readField(input.weight)
		};

		var out = {
			fields: fields,
			blocked: null,
			impossible: false,
			hasResult: false,
			height: null,
			weight: null,
			years: null,
			senior: false,
			low: null,
			high: null,
			floor: null,
			status: null,
			diff: null,
			note: null
		};

		// Blokada wieku ma pierwszeństwo (D4).
		var age = fields.age.value;
		if (age !== null && age < cfg.minor) {
			out.blocked = 'minor';
			return out;
		}

		// Tekst zamiast liczby w którymkolwiek polu: komunikat przy polu, bez wyniku.
		if (fields.age.invalid || fields.height.invalid || fields.weight.invalid) {
			return out;
		}
		if (age === null || fields.height.value === null) {
			return out;
		}

		var height = fields.height.value;
		var weight = fields.weight.value;
		var p = cfg.possible;
		// Wartości, których nie zanotowano u żadnego dorosłego (D10).
		if (height < p.height.min || height > p.height.max || (weight !== null && weight > p.weight.max)) {
			out.impossible = true;
			return out;
		}

		var range = weightRange(height, cfg);

		out.hasResult = true;
		out.height = height;
		out.weight = weight;
		out.years = Math.floor(age);
		out.senior = out.years >= cfg.senior.age_min;
		out.low = range.low;
		out.high = range.high;
		out.floor = seniorFloor(height, cfg);

		if (weight !== null) {
			var c = compare(weight, range);
			out.status = c.status;
			out.diff = c.diff;
		}
		out.note = note(out.senior, weight, height, out.status, out.floor, cfg);

		return out;
	}

	/**
	 * Stan pusty: przykłady z konfiguracji, zakres w pełnych kg.
	 */
	function samples(cfg) {
		return cfg.samples.map(function (s) {
			var range = weightRange(s.height, cfg);
			return { sample: s, low: range.low, high: range.high, floor: seniorFloor(s.height, cfg) };
		});
	}

	return {
		toNumber: toNumber,
		round1: round1,
		formatDecimal: formatDecimal,
		formatNumber: formatNumber,
		readField: readField,
		weightRange: weightRange,
		seniorFloor: seniorFloor,
		compare: compare,
		evaluate: evaluate,
		samples: samples
	};
}));
